import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Clock3, X, Trash2 } from "lucide-react";

const EMPTY_FORM = { name: "", default_rate: "", currency: "GBP", unit: "hour" };

function fmt(n, ccy = "GBP") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
  } catch {
    return `${ccy} ${(Number(n) || 0).toFixed(2)}`;
  }
}

export default function ActivityTypes() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/activity-types");
      setItems(data || []);
    } catch {
      toast.error("Failed to load activity types");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/activity-types", { ...form, default_rate: parseFloat(form.default_rate) || 0 });
      toast.success("Activity type created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create activity type");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await api.delete(`/activity-types/${item.activity_type_id}`);
      toast.success("Activity type deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete activity type");
    }
  };

  return (
    <DarkPage><div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Activity Types</h1>
          <p className="text-sm text-[#7d8590] mt-1">A rate catalog shared by the quote estimation matrix and activity-based billing — e.g. "Senior Developer" @ £150/hr.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Activity Type
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-[#7d8590] text-sm">No activity types yet. Add one to start pricing quotes with the estimation matrix.</div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#21262d]">
              {["Name", "Rate", "Unit", ""].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map(i => (
                <tr key={i.activity_type_id} className="border-b border-[#21262d] last:border-0">
                  <td className="px-4 py-3 text-white flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5 text-[#7d8590]" /> {i.name}</td>
                  <td className="px-4 py-3 text-white">{fmt(i.default_rate, i.currency)}</td>
                  <td className="px-4 py-3 text-[#7d8590]">per {i.unit}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(i)} className="p-1.5 text-[#7d8590] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Activity Type</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Senior Developer"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Default rate</label>
                  <input type="number" step="0.01" value={form.default_rate} onChange={e => setForm(f => ({ ...f, default_rate: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {["GBP", "USD", "EUR"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Unit</label>
                <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
